import {
  EventDispatcher,
  delegateEvent,
  toggleBooleanDOMAttr,
  addDisposableEventListener,
  useElementOrFindIt,
  Component,
} from '../core';
import { SELECTORS, CLASSES } from './constants';
const BREAKPOINT = 1023;

export interface GlobalNavOptions {
  globalNav?: HTMLElement;
  close?: HTMLElement;
  open?: HTMLElement;
  menu?: HTMLElement;
  backdrop?: HTMLElement;
  panel?: HTMLElement;
}

export interface GlobalNavEventMap {
  beforeHide: Element;
  afterHide: Element;
  beforeShow: Element;
  afterShow: Element;
}

export class GlobalNav implements Component<GlobalNavEventMap> {
  globalNav: HTMLElement;
  domElements: {
    navbar: HTMLElement;
    close: HTMLElement;
    open: HTMLElement;
    menu: HTMLElement;
    backdrop: HTMLElement;
    panel: HTMLElement;
  };
  private __dispatcher: EventDispatcher<GlobalNavEventMap>;
  private __eventDisposers: Array<() => void>;

  /**
   * @param {HTMLElement} globalNav
   */
  constructor(globalNav: HTMLElement, options: GlobalNavOptions) {
    this.globalNav = globalNav;
    this.domElements = {
      navbar: useElementOrFindIt(
        options && options.globalNav ? options.globalNav : null,
        {
          parent: this.globalNav,
          selector: SELECTORS.globalNav,
          throwIfNotFound: `Uxpl.GlobalNav could not be initilized because navbar element was not found ([${SELECTORS.globalNav}])`,
        }
      ),
      close: useElementOrFindIt(
        options && options.close ? options.close : null,
        {
          parent: this.globalNav,
          selector: SELECTORS.close,
        }
      ),
      open: useElementOrFindIt(options && options.open ? options.open : null, {
        parent: this.globalNav,
        selector: SELECTORS.open,
      }),
      menu: useElementOrFindIt(options && options.menu ? options.menu : null, {
        parent: this.globalNav,
        selector: SELECTORS.menu,
        throwIfNotFound: `Uxpl.GlobalNav could not be initilized because menu list element was not found ([${SELECTORS.menu}])`,
      }),
      backdrop: useElementOrFindIt(
        options && options.backdrop ? options.backdrop : null,
        {
          parent: this.globalNav,
          selector: SELECTORS.backdrop,
        }
      ),
      panel: useElementOrFindIt(
        options && options.panel ? options.panel : null,
        {
          parent: this.globalNav,
          selector: SELECTORS.panel,
        }
      ),
    };
    this.__dispatcher = new EventDispatcher();
    this.__attachListeners();
    this.__screenWidthChangeHandler();
  }

  addEventListener<K extends keyof GlobalNavEventMap>(
    eventName: K,
    listener: (event: GlobalNavEventMap[K]) => void
  ): void {
    this.__dispatcher.addEventListener(eventName, listener);
  }

  removeEventListener<K extends keyof GlobalNavEventMap>(
    eventName: K,
    listener: (event: GlobalNavEventMap[K]) => void
  ): void {
    this.__dispatcher.removeEventListener(eventName, listener);
  }

  destroy(): void {
    this.globalNav = null;
    this.domElements = null;
    this.__dispatcher.clearAll();
    this.__eventDisposers.forEach((dispose) => dispose());
  }

  isPanelOpen(): boolean {
    return (
      this.globalNav &&
      this.globalNav.classList &&
      this.globalNav.classList.contains(CLASSES.panelOpenClass)
    );
  }

  close(): void {
    this.__clearActiveItem();
    this.__togglePanelContent();
    this.globalNav.classList.toggle(CLASSES.panelOpenClass);
    toggleBooleanDOMAttr(this.domElements.panel, 'aria-hidden');
    toggleBooleanDOMAttr(this.domElements.backdrop, 'aria-hidden');
  }

  open(tabId: string): void {
    const menuActionAttr = '[' + SELECTORS.tabIdAttr + '="' + tabId + '"]';
    const menuAction = useElementOrFindIt(null, {
      parent: this.domElements.navbar,
      selector: menuActionAttr,
    });
    if (!menuAction) {
      console.warn(
        `Global Nav Component: could not find action with id "${tabId}"`
      );
      return;
    }
    if (this.isPanelOpen()) {
      const activeItem = this.domElements.menu.querySelector(
        `.${CLASSES.activeClass}`
      );
      this.__removeActiveState(activeItem);
      toggleBooleanDOMAttr(activeItem, 'aria-expanded');
      this.__togglePanelContent(menuAction, activeItem);
    } else {
      this.__togglePanelContent(menuAction);
      this.globalNav.classList.toggle(CLASSES.panelOpenClass);
      toggleBooleanDOMAttr(this.domElements.panel, 'aria-hidden');
      toggleBooleanDOMAttr(this.domElements.backdrop, 'aria-hidden');
    }
    this.__addActiveState(menuAction);
    toggleBooleanDOMAttr(menuAction, 'aria-expanded');
  }

  private __isBackdropOpen(): boolean {
    return (
      this.domElements.backdrop &&
      this.domElements.backdrop.classList &&
      this.domElements.backdrop.classList.contains(CLASSES.openClass)
    );
  }

  private __isPanelContentShown(panelContent?: Element): boolean {
    return (
      panelContent &&
      panelContent.classList &&
      panelContent.classList.contains(CLASSES.openClass)
    );
  }

  private __addActiveState(elem: Element) {
    elem.classList.add(CLASSES.activeClass);
  }

  private __removeActiveState(elem: Element) {
    elem.classList.remove(CLASSES.activeClass);
  }

  private __clearActiveItem(): void {
    const activeItem = this.domElements.menu.querySelector(
      `.${CLASSES.activeClass}`
    );
    if (activeItem !== null) {
      activeItem.classList.remove(CLASSES.activeClass);
      toggleBooleanDOMAttr(activeItem, 'aria-expanded');
    }
  }

  private __toggleVisibility(target: Element, isOpen: boolean): void {
    if (target) {
      target.classList[isOpen ? 'remove' : 'add'](CLASSES.openClass);
      target.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
    }
  }

  private __togglePanelContent(
    selectedMenuAction?: Element,
    activeMenuAction?: Element
  ) {
    if (this.domElements.panel) {
      const panelContents: Element[] = [];
      if (selectedMenuAction) {
        const menuActionAttrVal = selectedMenuAction.getAttribute(
          SELECTORS.tabIdAttr
        );
        const panelContent = this.domElements.panel.querySelector(
          `[ ${SELECTORS.tabIdAttr} = ${menuActionAttrVal} ]`
        );
        panelContents.push(panelContent);
      }
      if (activeMenuAction || (!selectedMenuAction && !activeMenuAction)) {
        const visiblePanelContent = this.domElements.panel.querySelector(
          `[ ${SELECTORS.tabIdAttr}]` + '.is-open'
        );
        panelContents.push(visiblePanelContent);
      }
      panelContents.forEach((panelContent) => {
        if (panelContent) {
          if (this.__isPanelContentShown(panelContent)) {
            this.__dispatcher.dispatchEvent('beforeHide', panelContent);
            this.__toggleVisibility(
              panelContent,
              this.__isPanelContentShown(panelContent)
            );
            this.__dispatcher.dispatchEvent('afterHide', panelContent);
          } else {
            this.__dispatcher.dispatchEvent('beforeShow', panelContent);
            this.__toggleVisibility(
              panelContent,
              this.__isPanelContentShown(panelContent)
            );
            this.__dispatcher.dispatchEvent('afterShow', panelContent);
          }
        }
      });
    }
  }

  private __menuActionHandler(elem: Element) {
    const activeItem = this.domElements.menu.querySelector(
      `.${CLASSES.activeClass}`
    );
    if (elem === activeItem) {
      this.close();
    } else {
      const id = elem.getAttribute(SELECTORS.tabIdAttr);
      this.open(id);
    }
  }

  private __screenWidthChangeHandler() {
    const screenWidth =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth;
    const ariaHiddenAttrOfOpen =
      this.domElements.open.getAttribute('aria-hidden');
    const ariaHiddenAttrOfMenu =
      this.domElements.menu.getAttribute('aria-hidden');
    if (screenWidth > BREAKPOINT) {
      if (ariaHiddenAttrOfOpen == 'false') {
        toggleBooleanDOMAttr(this.domElements.open, 'aria-hidden');
      }
      if (ariaHiddenAttrOfMenu == 'true') {
        toggleBooleanDOMAttr(this.domElements.menu, 'aria-hidden');
      }
    } else {
      if (ariaHiddenAttrOfOpen == 'true') {
        toggleBooleanDOMAttr(this.domElements.open, 'aria-hidden');
      }
      if (ariaHiddenAttrOfMenu == 'false') {
        toggleBooleanDOMAttr(this.domElements.menu, 'aria-hidden');
      }
    }
  }

  private __attachListeners() {
    this.__eventDisposers = [];

    this.__eventDisposers.push(
      delegateEvent(
        this.domElements.menu,
        'click',
        `[${SELECTORS.tabIdAttr}]`,
        this.__menuActionHandler.bind(this)
      )
    );

    if (this.domElements.backdrop) {
      this.__eventDisposers.push(
        addDisposableEventListener(
          this.domElements.backdrop,
          'click',
          this.close.bind(this)
        )
      );
    }

    if (this.domElements.close) {
      this.__eventDisposers.push(
        addDisposableEventListener(this.domElements.close, 'click', () => {
          this.globalNav.classList.toggle(CLASSES.panelOpenClass);
          toggleBooleanDOMAttr(this.domElements.panel, 'aria-hidden');
          toggleBooleanDOMAttr(this.domElements.open, 'aria-hidden');
          toggleBooleanDOMAttr(this.domElements.close, 'aria-hidden');
          toggleBooleanDOMAttr(this.domElements.menu, 'aria-hidden');
          this.__clearActiveItem();
          this.__togglePanelContent();
        })
      );
    }

    if (this.domElements.open) {
      this.__eventDisposers.push(
        addDisposableEventListener(this.domElements.open, 'click', () => {
          this.globalNav.classList.toggle(CLASSES.panelOpenClass);
          toggleBooleanDOMAttr(this.domElements.panel, 'aria-hidden');
          toggleBooleanDOMAttr(this.domElements.open, 'aria-hidden');
          toggleBooleanDOMAttr(this.domElements.close, 'aria-hidden');
          toggleBooleanDOMAttr(this.domElements.menu, 'aria-hidden');
          const firstMenuItem = this.domElements.menu.querySelector(
            `[${SELECTORS.tabIdAttr}]`
          );
          this.__addActiveState(firstMenuItem);
          this.__togglePanelContent(firstMenuItem);
        })
      );
    }
    this.__eventDisposers.push(
      addDisposableEventListener(
        window,
        'resize',
        this.__screenWidthChangeHandler.bind(this)
      )
    );
  }
}
